import React, { useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  doSignInWithEmailAndPassword,
  doSignInWithGoogle,
  doCreateUserWithEmailAndPassword,
  doPasswordReset,
} from '../firebase/Auth';
import { authError } from '../firebase/authErrors';
import MarketingHero from '../components/oracle/MarketingHero';
import AuthPanel from '../components/oracle/AuthPanel';
import FooterFeatureStrip from '../components/oracle/FooterFeatureStrip';
import LegalModal from '../components/legal/LegalModal';
import StructuredData from '../components/public/StructuredData';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useIsMobile from '../hooks/useIsMobile';
import { COPY } from '../contracts/copyMap';
import { ROUTES } from '../contracts/routes';
import { ORGANIZATION_LD, WEBSITE_LD } from '../contracts/structuredData';

// Sign-In / Sign-Up surface (design-recreation).
//
// Page owns auth-provider integration + validation; AuthPanel owns the form
// fields. The AuthContext userLoggedIn flag drives the inline <Navigate>
// (same proven pattern as the previous SignIn).
//
// Design-recreation strings not yet in contracts/copyMap (contracts are
// frozen for this surface rebuild). Migrate into copyMap when the contract
// next opens.
const LOCAL_COPY = {
  PASSWORD_MISMATCH: 'Passwords do not match. Confirm and resubmit.',
  RESET_NEEDS_EMAIL: 'Enter your email above to request a reset link.',
  RESET_SENT: 'PASSWORD RESET DISPATCHED. CHECK YOUR INBOX.',
};

function isValidInput(email, password) {
  if (typeof email !== 'string' || !email.includes('@')) return false;
  if (typeof password !== 'string' || password.length < 6) return false;
  return true;
}

const SignIn = () => {
  const location = useLocation();
  const { userLoggedIn } = useAuth();

  const [mode, setMode] = useState('sign-in');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [legalDoc, setLegalDoc] = useState(null); // 'tos' | 'privacy' | null

  // Mobile only: the specimen verdict holds the first screen and hands over to
  // the auth panel on tap. Desktop shows both side by side and never enters
  // this state. See the MarketingHero header for why.
  const isMobile = useIsMobile();
  const [introDismissed, setIntroDismissed] = useState(false);

  // Before the early return below — hooks must run unconditionally.
  useDocumentTitle(COPY.SIGN_IN_PAGE_TITLE);

  const enterAuth = (nextMode) => {
    setError(null);
    setNotice(null);
    setMode(nextMode);
    setIntroDismissed(true);
    // The intro is taller than the auth panel, so without this the user is left
    // scrolled mid-page with the wordmark cut off after the handover.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const destination = (location.state && location.state.from) || ROUTES.ORACLE;

  if (userLoggedIn) {
    return <Navigate to={destination} replace />;
  }

  const handleGoogle = async () => {
    if (submitting) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await doSignInWithGoogle();
      // userLoggedIn will flip on the next render; inline Navigate handles routing.
    } catch (err) {
      setError(authError(err, COPY.SIGN_IN_GENERIC_ERROR));
      setSubmitting(false);
    }
  };

  const handleSubmit = async (currentMode, { email, password, confirmPassword }) => {
    if (submitting) return;
    setNotice(null);
    if (!isValidInput(email, password)) {
      setError(COPY.SIGN_IN_INVALID_EMAIL);
      return;
    }
    if (currentMode === 'sign-up' && password !== confirmPassword) {
      setError(LOCAL_COPY.PASSWORD_MISMATCH);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (currentMode === 'sign-in') {
        await doSignInWithEmailAndPassword(email, password);
      } else {
        await doCreateUserWithEmailAndPassword(email, password);
      }
    } catch (err) {
      setError(authError(err, COPY.SIGN_IN_GENERIC_ERROR));
      setSubmitting(false);
    }
  };

  const handleForgot = async (email) => {
    if (submitting) return;
    setNotice(null);
    if (typeof email !== 'string' || !email.includes('@')) {
      setError(LOCAL_COPY.RESET_NEEDS_EMAIL);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await doPasswordReset(email);
    } catch (err) {
      // Anti-enumeration: the reset endpoint returns success for unknown
      // addresses on purpose, so a distinct failure banner here would leak
      // which emails have accounts (existing -> success, unknown -> error).
      // Always show the same confirmation; log the real failure for debugging.
      console.error('Password reset dispatch failed:', err);
    } finally {
      setNotice(LOCAL_COPY.RESET_SENT);
      setSubmitting(false);
    }
  };

  const handleModeChange = (next) => {
    setError(null);
    setNotice(null);
    setMode(next);
  };

  return (
    <main>
      {/* `/` and /sign-in are the highest-traffic surface in the product (87%
          of sessions) and shipped with the generic global <title> and no
          structured data. */}
      <StructuredData id="ld-organization" data={ORGANIZATION_LD} />
      <StructuredData id="ld-website" data={WEBSITE_LD} />
      <MarketingHero
        footer={<FooterFeatureStrip />}
        showIntro={isMobile && !introDismissed}
        onEnterAuth={enterAuth}
      >
        <AuthPanel
          mode={mode}
          onModeChange={handleModeChange}
          onSubmit={handleSubmit}
          onGoogle={handleGoogle}
          onForgot={handleForgot}
          submitting={submitting}
          error={error}
          notice={notice}
          onOpenLegal={setLegalDoc}
        />
      </MarketingHero>
      <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />
    </main>
  );
};

export default SignIn;
